import React, { Component } from 'react';
import SelectionContext from './state/SelectionContext';
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MapPage from './MapPage';
import ClientPage from './ClientPage';
import Sidebar from './Sidebar';
import ReportPage from './ReportPage';
import MapReportPreview from './MapReportPreview';
import MapReportPopup from './MapReportPopup';
import { getPolConfig, getPolColours, DEMO_VARIANT_CLIENT_GROUPS } from './utils/polAggregators';
import { loadGeo } from "./utils/mapPage/loadGeo";
import HeaderBar from './HeaderBar';
import GuidedTour, { resetTour } from './GuidedTour';
import UserMenu from './UserMenu';
import IndicatorsPage from "./IndicatorsPage";
import { buildLabelsAndColors } from "./utils/colorScale";
import { DEMO_LABELS } from "./utils/demographicLabels";
import { fetchExtData as fetchExtDataUtil } from "./utils/fetchExtData";
import { isTrackingSchema } from "./utils/schemaTypes";
import { isTopLevel } from "./utils/geoLevels";
import { segmentFilterCarriesOver, segmentLabel } from "./utils/segmentConfig";
import axios from 'axios';

// AI chat tab is visible only to Stack Data Strategy users (is_staff + company_slug matches)
const ENABLE_CHAT = true;
const CHAT_ALLOWED_SLUG = "stack-data-strategy";
const INITIAL_CHAT_MESSAGES = [
  {
    role: "assistant",
    content:
      'Ask me about the survey data — things like "Which constituencies have the highest Labour vote intention?" or "What are the top local issues in London seats?"',
    queryUsed: null,
  },
];

const SIDEBAR_WIDTH = 500;
const SIDEBAR_COLLAPSED = 32;

function slugify(str) {
  return str
    ?.toString()
    .trim()
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const THEME_CONFIGS = {
  explore: {
    label: 'Tracking Data',
    config: getPolConfig,
    defaultVar: 'vote_intention',
    colours: getPolColours,
  },
  indicators: {
    label: 'Indicators',
  },
  report: {
    label: 'Report',
  },
  client: { label: "Client" }, // default text before user loads


};
const ALLOWED = new Set(Object.keys(THEME_CONFIGS));

// Maps a question/group's `category` to the report type shown by default in
// the map report preview. Anything unmapped falls back to the political report.
const CATEGORY_TO_REPORT_TYPE = {
  political: "default",
  business: "business",
  motability: "motability",
};

function themeFromPath(pathname) {
  const seg = (pathname || '/').replace(/^\//, '').split('/')[0];
  return ALLOWED.has(seg) ? seg : 'explore';
}

function sectionFromSlug(slug) {
  if (ALLOWED.has(slug)) return slug;

  // anything else means "client page"
  return "client";
}



class MainPage extends Component {
  static contextType = SelectionContext;

 constructor(props) {
    super(props);

    this.mapRef = React.createRef();

    // Prefer slug passed via props from React Router
    const slug = props.slug;
     const initialSection = sectionFromSlug(slug);
    const themeEntry = THEME_CONFIGS[initialSection];

    this.state = {
      tourForceRun: 0,
      isPanelOpen: true,
      selectedSection: initialSection,
      sectionTitle: themeEntry.label,
      user: { email: '', name: '', company: '' },

      rankedItems: [],
      mapLoading: false,
      baselineLoading: false,
      selectedAreaIdToZoom: null,
      possibleAreas: null,
      geoLevel: "constituency",
      // The top level the map drilled in from, so constituency-only features
      // (report preview, chat context, census comparison) can tell a seat from
      // a local-authority district. See handleFeatureSelect.
      selectedParentLevel: null,
      activeClientSlug: props.slug,   // <- save it!
      ActionTargetConcentration: "all",

      gender: null,
      age: null,
      ethnicity: null,
      party: null,
      education: null,
      segment: [],

      extDataBlurb: null,
      extDataBlurbComparison: null,

      // Defer rendering client view until user/config are ready
      isBootstrapping: true,
      audienceInsight: null,
      audienceInsightLoading: false,
      audienceInsightRequestId: 0,

      
      compareWaves: false,
      showArrows: true,
      compareKey: null,
      isDemoUser: false,
      chatHighlightAreas: [],
      chatShouldZoom: false,
      pendingChatNavigation: null,
      activeTab: "data",
      chatMessages: INITIAL_CHAT_MESSAGES,
      chatInput: "",
      chatLoading: false,
      chatQueryOpen: null,
      providerFilter: null,
      providerFilterOriginalVar: null,

      // Report preview shown over the map when a constituency is selected.
      // reportType defaults to the political report; the preview expands into
      // a full report view on click.
      previewReportType: "default",
      reportPreviewExpanded: false,

      // One-shot config handed to ReportPage when the studio is opened pre-loaded
      // (e.g. the map "Custom" tab launching a drill-down on the active question).
      reportInitialConfig: null,

    };

    if (themeEntry.config) {
      // Directly assign — setState is a no-op in the constructor (React 18)
      Object.assign(this.state, this._buildConfigState(themeEntry));
    }
  }

  flyTo = (options) => {
    this.mapRef?.current?.flyTo?.(options);
  };


  /**
   * setHighlightsState — single-call map state setter for the Q2 highlights page.
   * Reads groupsById safely so it never crashes on batched-setState timing issues.
   *
   * @param {{ variable: string, compareWaves?: boolean, compareKey?: string, waveSelect?: number }} config
   */
  setHighlightsState = (config) => {
    const { variable, compareWaves = false, compareKey, waveSelect, preferenceFilter: pfOverride, highlightAreas } = config;
    const group = this.state.groupsById?.[variable];
    if (!group) {
      // groupsById not yet populated (or variable not found) — retry once
      if (!config._retried) {
        setTimeout(() => this.setHighlightsState({ ...config, _retried: true }), 400);
      }
      return;
    }

    const variableType = group.type;
    let preferenceFilter;
    if (pfOverride) {
      preferenceFilter = pfOverride;
    } else if (variableType === "likert") {
      preferenceFilter = "net";
    } else if (variableType === "likert_grid") {
      preferenceFilter = "compare";
    } else {
      preferenceFilter = "top";
    }

    // Fall back to the latest available wave if the requested wave doesn't exist
    const availableWaves = group.waves;
    let resolvedWave;
    if (availableWaves?.length) {
      const latest = [...availableWaves].sort((a, b) => b - a)[0];
      resolvedWave = (waveSelect !== undefined && availableWaves.includes(waveSelect))
        ? waveSelect
        : latest;
    }

    this.setState({
      selectedVariable: variable,
      preferenceFilter,
      compareWaves,
      compareKey: compareKey ?? (compareWaves ? "__top__" : this.state.compareKey),
      ...(resolvedWave !== undefined ? { waveSelect: resolvedWave, waveOptions: availableWaves } : {}),
      chatHighlightAreas: highlightAreas ?? [],
      chatShouldZoom: false,
    });
  };

  _buildConfigState = (themeEntry) => {
  const { groupsById } = themeEntry.config();
  const theme_colours = themeEntry.colours();
  const { labels, colors } = buildLabelsAndColors(groupsById, theme_colours);

  const defaultVar = themeEntry.defaultVar;
  const variableType = groupsById[defaultVar].type;

  const preferenceFilter = variableType === "likert" ? "net" : "top";
  const randomKey = Object.keys(groupsById).find((k) => k !== defaultVar);
  const variableSchema = groupsById[defaultVar].schema;
  const Waves = groupsById[defaultVar]?.waves;
  const latest_wave = Waves?.sort((a, b) => b - a)[0];

  return {
    groupsById,
    selectedVariable: defaultVar,
    viewMode: "heat",
    preferenceFilter,
    theme_colours,
    colorLabels: labels,
    colorMap: colors,
    ActionTargetVariables: {
      TargetVariable1: defaultVar,
      TargetVariable2: randomKey,
    },
    ActionTargetSubVariables: {
      TargetSubVariable1:
        groupsById[defaultVar]?.type === "likert" && groupsById[defaultVar]?.polarity
          ? "net"
          : groupsById[defaultVar].keys[0],
      TargetSubVariable2:
        groupsById[randomKey]?.type === "likert" && groupsById[randomKey]?.polarity
          ? "net"
          : groupsById[randomKey].keys[0],
    },
    ActionTargetRanges: {
      TargetRange1: [0, 1000],
      TargetRange2: [0, 1000],
    },
    ActionTargetMAXRanges: {
      TargetMAXRange1: [0, 1],
      TargetMAXRange2: [0, 1],
    },
    ActionTargetConcentration: "all",
    ActionMode: "explore",
    audienceInsight: null,
    audienceInsightLoading: false,
    audienceInsightRequestId: 0,
    compareKey: "__top__",
    ...(isTrackingSchema(variableSchema) ? { waveOptions: Waves, waveSelect: latest_wave } : {}),
  };
};

  initializeConfig = (themeEntry) => {
  this.setState(this._buildConfigState(themeEntry), () => {
    this.fetchExtData();
  });
};



  handleRankedUpdate = (items) => this.setState({ rankedItems: items });
  handleMapLoadingChange = (loading) => this.setState({ mapLoading: loading });
  handleBaselineLoadingChange = (loading) => this.setState({ baselineLoading: loading });
  handleAreaSelect   = (areaId) => this.setState({ selectedAreaIdToZoom: areaId });
  handleActionTargetConcentrationChange = (val) =>
    this.setState({ ActionTargetConcentration: val });
  handleAudienceInsightRequest = () =>
    this.setState((prev) => ({ audienceInsightRequestId: prev.audienceInsightRequestId + 1 }));
  componentDidMount() {
        this._onPopState = () => {
      const currentSlug = window.location.pathname.replace(/^\//, "");
      const selectedSection = sectionFromSlug(currentSlug);
      this.applyTheme(selectedSection);
    };
    window.addEventListener("popstate", this._onPopState);
  
    if (this.props.skipAuthRedirect) {
      this.initializeConfig(THEME_CONFIGS.explore);
      this.setState({ isBootstrapping: false });
    } else
    axios.get("/api/user-info/")
      .then((response) => {
        const user = response.data;
        if (!user || Object.keys(user).length === 0) {
          window.location.href = "/";
        } else {
          const isDemoUser = Boolean(user?.is_demo_user);
          const demoVariant = String(user?.demo_variant || "default").trim().toLowerCase();
          const demoCanAccessClient = !isDemoUser || demoVariant !== "default";
          this.setState({ user, isDemoUser }, () => {
          const dynamicSlug = this.state.user?.company_slug || slugify(this.state.user.company) || "client";
          this.setState({ dynamicSlug });

          const requestedSlug = this.props.slug;

          // FIRST: handle access rules
          if (!this.clientIsAllowed() && !ALLOWED.has(requestedSlug)) {
            this.setThemeInUrl("explore", { force: true });
            // Ensure core config is available for downstream components
            if (!this.state.groupsById && THEME_CONFIGS.explore?.config) {
              this.initializeConfig(THEME_CONFIGS.explore);
            }
            this.setState({ isBootstrapping: false });
            return;
          }

          // EVEN IF ACCESS IS ALLOWED — explicitly force apply the real section
          const realSection = sectionFromSlug(requestedSlug);
          if (!demoCanAccessClient && realSection === "client") {
            this.setThemeInUrl("explore", { force: true });
            this.setState({ isBootstrapping: false });
            return;
          }
          this.applyTheme(realSection, { force: true });

          // Ensure groups exist even for client section so filtering works
          if (!this.state.groupsById && THEME_CONFIGS.explore?.config) {
            this.initializeConfig(THEME_CONFIGS.explore);
          }

          this.setState({ isBootstrapping: false });
        });
        };
        
      })
      .catch((error) => {
        console.error("Error fetching user info:", error);
        if (!this.props.skipAuthRedirect) window.location.href = "/";
      });
      this.fetchExtData()
  
    // ✅ Preload constituencies once
    loadGeo({
      level: "constituency",
      setState: this.setState.bind(this),
      mapRef: { current: null }, // no map required
      fitToGeo: () => {},
      normalizeId: (level, props) => props.PCON24CD,
      handlePossibleAreasChange: this.handlePossibleAreasChange,
    });
  }
  
  
    staffCanSeeClientPages() {
  const { user } = this.state;
  return user.is_staff === true;     // internal team
}

clientIsAllowed() {
  const { user } = this.state;
  if (user.has_client_access === true) return true;
  if (!this.staffCanSeeClientPages()) return false;
  // TEMP: Stack staff can only see their own tab, not external clients (e.g. Motability)
  // Flip STACK_CAN_SEE_ALL_CLIENTS to true to re-enable full access
  const STACK_CAN_SEE_ALL_CLIENTS = false;
  if (!STACK_CAN_SEE_ALL_CLIENTS && user?.company_slug === "stack-data-strategy") {
    const currentSlug = this.props.slug;
    // Only block if on an actual external client slug (not explore/report/indicators/etc.)
    const isExternalClientSlug = currentSlug && !ALLOWED.has(currentSlug) && currentSlug !== "stack-data-strategy";
    return !isExternalClientSlug;
  }
  return true;
}
  

  componentWillUnmount() {
    window.removeEventListener('popstate', this._onPopState);
  }

  componentDidUpdate(prevProps, prevState) {
      if (this.props.slug !== prevProps.slug) {
        const newSection = sectionFromSlug(this.props.slug);
        const demoVariant = String(this.state.user?.demo_variant || "default").trim().toLowerCase();
        const demoCanAccessClient = !this.state.isDemoUser || demoVariant !== "default";
        if (!demoCanAccessClient && newSection === "client") {
          this.setThemeInUrl("explore", { force: true });
          return;
        }

        // force full reinitialisation
        this.applyTheme(newSection, { force: true });
        this.setState({ activeClientSlug: this.props.slug });
      }
    
    const { selectedFeature, selectedconstName } = this.state;
  
    const featureChanged =
      selectedFeature !== prevState.selectedFeature ||
      (selectedFeature?.level !== prevState.selectedFeature?.level);
  
    const constChanged = selectedconstName !== prevState.selectedconstName;
  
    // only trigger fetch when user selects something meaningful
    if ((featureChanged || constChanged) && selectedFeature) {
      const level = selectedFeature.level;
      // External census/election data is published per constituency and ward
      // only, and its ward rows are compared against the parent constituency —
      // so skip it entirely when the drill-in came from a local authority.
      const hasConstituencyContext =
        !this.state.selectedParentLevel ||
        this.state.selectedParentLevel === "constituency";
      if ((level === "ward" || level === "constituency") && hasConstituencyContext) {
        this.fetchExtData();
      }
    }

    const pendingChatNavigation = this.state.pendingChatNavigation;
    const pendingChanged = pendingChatNavigation !== prevState.pendingChatNavigation;
    const sectionChanged = this.state.selectedSection !== prevState.selectedSection;
    const groupsChanged = this.state.groupsById !== prevState.groupsById;

    if ((pendingChanged || sectionChanged || groupsChanged) && pendingChatNavigation) {
      const { metric, targetSection, queryUsed } = pendingChatNavigation;
      // targetSection is "explore", "client", or a specific slug like "stack-data-strategy".
      // We're ready when the current section matches — "client" covers any client slug.
      const currentSection = this.state.selectedSection;
      const isReadyForMetric =
        !targetSection ||
        currentSection === targetSection ||
        (targetSection === "client" && currentSection === "client") ||
        (sectionFromSlug(targetSection) === "client" && currentSection === "client");
      if (isReadyForMetric && metric && this.state.groupsById?.[metric]) {
        this.setState({ pendingChatNavigation: null }, () => {
          this.applyChatMetricSelection(metric, queryUsed);
        });
      }
    }

    if (prevState.selectedVariable !== this.state.selectedVariable) {
      this.context.setSelectedVariable(this.state.selectedVariable);
      // Follow the question: default the report preview to the report type
      // matching the newly-selected variable's category. Manual dropdown
      // picks still override until the variable changes again.
      const nextReportType = this.getReportTypeForVariable(this.state.selectedVariable);
      if (nextReportType !== this.state.previewReportType) {
        this.setState({ previewReportType: nextReportType });
      }
    }
    if (prevState.waveSelect !== this.state.waveSelect) {
      this.context.setWaveSelect(this.state.waveSelect);
    }
    if (prevState.preferenceFilter !== this.state.preferenceFilter) {
      this.context.setPreferenceFilter(this.state.preferenceFilter);
    }
  }
  
  setThemeInUrl = (theme, options = {}) => {
  const slug =
    theme === "client"
      ? (this.state.dynamicSlug || "client")
      : theme;

  const newUrl = '/' + slug;

  // Navigate via React Router so params (this.props.slug) update properly
  if (this.props.navigate) {
    if (window.location.pathname !== newUrl) {
      this.props.navigate(newUrl);
    }
  } else {
    // Fallback to pushState if navigate not provided
    if (window.location.pathname !== newUrl) {
      window.history.pushState({}, '', newUrl);
    }
  }

  // Reapply theme immediately (navigate will also re-render)
  this.applyTheme(theme, options);
};

applyTheme = (section, options = {}) => {
  const themeEntry = THEME_CONFIGS[section];
  if (!themeEntry) return;

  const mustReinit =
    options.force === true ||
    this.state.selectedSection !== section;

  // Always set core section state; reset to explore mode on every tab switch
  this.setState({
    selectedSection: section,
    sectionTitle: themeEntry.label,
    ActionMode: "explore",
  });

  // Reinitialise config when needed
  if (mustReinit && themeEntry.config) {
    this.initializeConfig(themeEntry);
  }
  //const selectedVariable =  themeEntry?.defaultVar;
  //this.handleCompareKeyChange( this?.state?.groupsById[selectedVariable]?.keys[0])

  // Special behaviour for the "report" section
  if (section === "report") {
    this.setState({
      selectedFeature: null,
      selectedconstName: null,
      PossibleAreas: [],       // clear ward data
      geoLevel: "constituency",
    });

    // Reload constituency geometry
    loadGeo({
      level: "constituency",
      setState: this.setState.bind(this),
      mapRef: { current: null },
      fitToGeo: () => {},
      normalizeId: (level, props) => props.PCON24CD,
      handlePossibleAreasChange: this.handlePossibleAreasChange,
    });

    const map = this.mapRef?.current;

    map?.fetchConstituenciesBaseline?.();

    map?.fetchMetrics?.("constituency", null);
  }
};


  handleFeatureSelect = ({ feature, data, constCode, constName }) => {
    this.setState((prev) => {
      // Which top level the map drilled in from. Selecting a top-level area
      // sets it; a ward selected underneath inherits it, since the ward's own
      // feature says nothing about which parent got you there.
      const nextParentLevel = isTopLevel(feature?.level)
        ? feature.level
        : (feature ? prev.selectedParentLevel : null);

      // Resolve the constituency code even when a caller omits constCode.
      // A constituency feature carries the code as `value`; when a ward is
      // selected we keep the parent constituency. Only clear when the whole
      // selection is cleared (feature is null) — otherwise late-arriving
      // onFeatureSelect calls (e.g. after map data finishes loading) would
      // null it out and make the report preview vanish.
      //
      // Wards also drill down from local authorities, and everything keyed on
      // selectedconstCode — the report preview, the chat context, the external
      // census comparison — is constituency-only. A district code is not a
      // seat, so under a local-authority parent there is no constituency in
      // play and the code is left null rather than filled with the district's.
      let nextConstCode;
      if (nextParentLevel && nextParentLevel !== "constituency") nextConstCode = null;
      else if (constCode) nextConstCode = constCode;
      else if (feature?.level === "constituency" && feature?.value) nextConstCode = feature.value;
      else if (feature?.properties?.PCON24CD) nextConstCode = feature.properties.PCON24CD;
      else if (feature) nextConstCode = prev.selectedconstCode;
      else nextConstCode = null;

      return {
        selectedFeature: feature || null,
        selectedFeatureData: data || { national: [], constituency: [], selected: [], nValues: { national: null, constituency: null, selected: null } },
        selectedconstName: constName || (feature ? prev.selectedconstName : null),
        selectedconstCode: nextConstCode,
        selectedParentLevel: nextParentLevel,
      };
    });
  };

  // Report preview handlers. The preview box is driven off the selected
  // constituency; these just manage the report-type choice and expand state.
  // Derives the default report type from a variable's category (political →
  // political report, business → business report, etc.).
  getReportTypeForVariable = (variableId) => {
    const category = this.state.groupsById?.[variableId]?.category;
    return CATEGORY_TO_REPORT_TYPE[category] || "default";
  };
  handlePreviewReportTypeChange = (reportType) => this.setState({ previewReportType: reportType });
  openReportPreview = () => this.setState({ reportPreviewExpanded: true });
  closeReportPreview = () => this.setState({ reportPreviewExpanded: false });
  // Close the popup and navigate to the full Report page.
  goToReportPage = () => {
    this.setState({ reportPreviewExpanded: false, reportInitialConfig: null });
    this.setThemeInUrl("report");
  };

  // Launch the Reports studio straight into a question drill-down for the
  // currently-selected map question + constituency (map "Custom" tab).
  goToReportDrilldown = (groupId) => {
    if (!groupId || !this.state.selectedconstCode) return;
    this.setState({
      reportPreviewExpanded: false,
      reportInitialConfig: {
        mode: "custom",
        customType: "drilldown",
        drillGroupId: groupId,
        areaScope: "constituency",
        selConstituency: {
          name: this.state.selectedconstName,
          area_cd: this.state.selectedconstCode,
          code: this.state.selectedconstCode,
        },
      },
    });
    this.setThemeInUrl("report");
  };
  




  togglePanel = () => {
    this.setState(
      s => ({ isPanelOpen: !s.isPanelOpen }),
      () => {
        let elapsed = 0;
        const interval = setInterval(() => {
          window.dispatchEvent(new Event('resize'));
          elapsed += 50;
          if (elapsed >= 350) clearInterval(interval);
        }, 50);
      }
    );
  };

  

  handleVariableChange = (val) => {
    const themeEntry = THEME_CONFIGS[this.state.selectedSection];
    const selectedVariable = val || themeEntry.defaultVar;
    const variableType = this.state.groupsById[selectedVariable]?.type;
    const variableSchema = this.state.groupsById[selectedVariable].schema;

    // Clear provider filter when user manually changes to a different question
    const providerClear = { providerFilter: null, providerFilterOriginalVar: null };

    let preferenceFilter;

    if (variableType === "price_elast") {
      preferenceFilter = this.state.groupsById[selectedVariable]?.polarity?.expect;
    } else if (variableType === "likert") {
      preferenceFilter = "net";
    } else if (variableType === "likert_grid") {
      preferenceFilter = "compare";
    } else {
      preferenceFilter = "top";
    }
    const Waves = this.state.groupsById[selectedVariable]?.waves
    const latest_wave = Waves?.sort((a, b) => b - a)[0]
     this.handleCompareKeyChange("__top__")

    // A segment code only means something relative to its schema (4 is
    // "Nostalgic Populists" under tracking, "Octopus Energy" under energy), so
    // an active selection must be dropped when the underlying column changes —
    // otherwise the filter silently starts meaning something else.
    const prevSchema = this.state.groupsById[this.state.selectedVariable]?.schema;
    const segmentReset = segmentFilterCarriesOver(prevSchema, variableSchema)
      ? {}
      : { segment: [] };

    // Questions that hide the demographic panel must not carry a filter in from
    // the previous question — the panel would be gone but the filter still live,
    // so the numbers would silently be a subgroup with no way to see or clear it.
    const demoReset = this.state.groupsById[selectedVariable]?.hideDemographicFilters
      ? { gender: null, age: null, ethnicity: null, party: null, education: null, segment: [] }
      : {};

    this.setState(
      {
        selectedVariable,
        preferenceFilter,
        ...segmentReset,
        ...demoReset,
        viewMode: ["top", "second", "third"].includes(preferenceFilter)
          ? "top"
          : "heat",
          ...(isTrackingSchema(variableSchema)
            ? { waveOptions: Waves, waveSelect: latest_wave }
            : { compareWaves: false, waveOptions: null, waveSelect: null }),
        ...providerClear,
      },
      () => {
        // MapPage.componentDidUpdate handles fetchMetrics + fetchConstituenciesBaseline
        // when selectedVariable changes — no need to call them manually here.
        // Calling them from MainPage caused a race condition at ward level because
        // MainPage.geoLevel is "constituency" while MapPage.geoLevel is "ward".
      }
    );
  };

  handleProviderFilterChange = (providerKey) => {
    const { groupsById, selectedVariable, providerFilterOriginalVar } = this.state;
    const originalVar = providerFilterOriginalVar || selectedVariable;
    const config = groupsById?.[originalVar]?.providerFilter;
    if (!config) return;

    if (providerKey === null) {
      // "All" selected — revert to original variable
      this.setState({
        selectedVariable: originalVar,
        providerFilter: null,
        providerFilterOriginalVar: null,
      });
    } else if (providerKey === "compare") {
      // Compare mode — keep showing the original variable on the map
      this.setState({
        selectedVariable: originalVar,
        providerFilter: "compare",
        providerFilterOriginalVar: originalVar,
      });
    } else {
      const provider = config.providers.find((p) => p.key === providerKey);
      if (!provider) return;
      // Swap the displayed variable without going through handleVariableChange
      // (which would reinitialise sections/waves and potentially lose the client context)
      this.setState({
        selectedVariable: provider.variable,
        providerFilter: providerKey,
        providerFilterOriginalVar: originalVar,
      });
    }
  };

  fetchExtData = async () => {
    try {
      const { selectedFeature, selectedAreaLabel, selectedconstCode, selectedconstName } = this.state;
      const { data, dataComparison } = await fetchExtDataUtil({
        selectedFeature, selectedAreaLabel, selectedconstCode, selectedconstName,
      });
      this.setState({ extDataBlurb: data, extDataBlurbComparison: dataComparison });
    } catch (err) {
      console.error("Failed to fetch external data:", err);
      this.setState({ extDataBlurb: null, extDataBlurbComparison: null });
    }
  };


  
  
  

  handleActionTargetVariableChange = ({val,varId}) => {
    const themeEntry =  THEME_CONFIGS[this.state.selectedSection];
    const selectedVariable = val || themeEntry.defaultVar;
    // Look up in both explore groupsById and client groups (e.g. Motability tab)
    const clientSlug = this.state.dynamicSlug;
    const clientGroups = this.filterByClient(this.state.groupsById, clientSlug) || {};
    const allAvailableGroups = { ...this.state.groupsById, ...clientGroups };
    const selectedGroup = allAvailableGroups[selectedVariable];
    const variableType = selectedGroup?.type;
    const variableSchema = selectedGroup?.schema;
    const waves = Array.isArray(selectedGroup?.waves) ? [...selectedGroup.waves].sort((a, b) => b - a) : [];
    const latestWave = waves[0] ?? null;



      let preferenceFilter;
      switch(variableType){
      case "price_elast":
        preferenceFilter = selectedGroup.polarity.expect
        break
      case "likert":
        preferenceFilter = selectedGroup?.polarity ? "net" : selectedGroup?.["keys"][0]
        break
      case "likert_grid":
        preferenceFilter = "compare"
        break
      default:
        preferenceFilter = selectedGroup?.["keys"][0]
        break
      }


        this.setState((prevState) => {
          const nextSubVar1 =
            varId === "1" ? preferenceFilter : prevState.ActionTargetSubVariables.TargetSubVariable1;
          const nextSubVar2 =
            varId === "2" ? preferenceFilter : prevState.ActionTargetSubVariables.TargetSubVariable2;

            
          return {
            ActionTargetVariables: {
              TargetVariable1:
                varId === "1" ? selectedVariable : prevState.ActionTargetVariables.TargetVariable1,
              TargetVariable2:
                varId === "2" ? selectedVariable : prevState.ActionTargetVariables.TargetVariable2,
            },
            ActionTargetSubVariables: {
              TargetSubVariable1: nextSubVar1,
              TargetSubVariable2: nextSubVar2,
            },
            ActionTargetRanges: {
              TargetRange1: nextSubVar1 === "net" ? [-1, 1] : [0, 1000],
              TargetRange2: nextSubVar2 === "net" ? [-1, 1] : [0, 1000],
            },
            ...(varId === "1" && isTrackingSchema(variableSchema)
              ? {
                  waveOptions: waves,
                  waveSelect: latestWave,
                  compareWaves: false,
                }
              : {}),
          };
        });


    
  };

  handleActionTargetSubVariableChange = ({val,varId}) => {

        
        this.setState((prevState) => {
          const nextSubVar1 =
            varId === "1" ? val : prevState.ActionTargetSubVariables.TargetSubVariable1;
          const nextSubVar2 =
            varId === "2" ? val : prevState.ActionTargetSubVariables.TargetSubVariable2;

          return {
              ActionTargetSubVariables: {
                TargetSubVariable1:
                  varId === "1"
                    ? val
                    : this.state.ActionTargetSubVariables.TargetSubVariable1,
                TargetSubVariable2:
                  varId === "2"
                    ? val
                    : this.state.ActionTargetSubVariables.TargetSubVariable2,
              },
            ActionTargetRanges: {
              TargetRange1: nextSubVar1 === "net" ? [-1000, 1000] : [0, 1000],
              TargetRange2: nextSubVar2 === "net" ? [-1000, 1000] : [0, 1000],
            },
          }
      });

    
  };
  handleActionTargetRangesChange = ({ val, varId }) => {
    this.setState((prevState) => ({
      ActionTargetRanges: {
        TargetRange1:
          varId === "1" ? val : prevState.ActionTargetRanges.TargetRange1,
        TargetRange2:
          varId === "2" ? val : prevState.ActionTargetRanges.TargetRange2,
      },
    }));
  };

handleActionTargetMAXRangesChange = ({ val, varId }) => {
  const safeVal = Array.isArray(val)
    ? val.map((n, i) => (isNaN(n) ? (i === 0 ? 0 : 1) : n)) 
    : [0, 1];

  this.setState((prevState) => ({
    ActionTargetMAXRanges: {
      TargetMAXRange1:
        varId === "1"
          ? safeVal
          : prevState.ActionTargetMAXRanges?.TargetMAXRange1 ?? [0, 1],
      TargetMAXRange2:
        varId === "2"
          ? safeVal
          : prevState.ActionTargetMAXRanges?.TargetMAXRange2 ?? [0, 1],
    },
  }));
};
  handleGenderChange     = (val) => this.setState({ gender: val });
  handleAgeChange        = (val) => this.setState({ age: val });
  handleEthnicityChange  = (val) => this.setState({ ethnicity: val });
  handlePartyChange      = (val) => this.setState({ party: val });
  handleEducationChange  = (val) => this.setState({ education: val });
  handleSegmentChange    = (val) => this.setState({ segment: val });
  handleActionModeChange = (val) => {
    if (val === "filter") {
      const selectedVariable =
        this.state.selectedVariable || THEME_CONFIGS[this.state.selectedSection]?.defaultVar;
      const variableType = this.state.groupsById?.[selectedVariable]?.type;
      let preferenceFilter;
      switch (variableType) {
        case "price_elast":
          preferenceFilter = this.state.groupsById?.[selectedVariable]?.polarity?.expect;
          break;
        case "likert":
          preferenceFilter = this.state.groupsById?.[selectedVariable]?.polarity
            ? "net"
            : this.state.groupsById?.[selectedVariable]?.keys?.[0];
          break;
        case "likert_grid": {
          // Default to the first sector (top-left in the drill grid) on net —
          // target mode always needs one concrete sector, never compare-all.
          const firstSub = this.state.groupsById?.[selectedVariable]?.subVariables?.[0];
          preferenceFilter = firstSub ? `${firstSub}|net` : "compare";
          break;
        }
        default:
          preferenceFilter = this.state.groupsById?.[selectedVariable]?.keys?.[0];
          break;
      }
      // Carry the current preferenceFilter if it's a specific key (not an aggregate),
      // so "filter by response → find target areas" preserves the selected option.
      const SPECIAL_FILTERS = new Set(["top", "second", "third", "net", "compare"]);
      const currentFilter = this.state.preferenceFilter;
      const isCarryable = currentFilter &&
        !SPECIAL_FILTERS.has(String(currentFilter)) &&
        !String(currentFilter).startsWith("compare_");
      let targetSubVar1 = isCarryable ? currentFilter : preferenceFilter;
      // Likert grids: keep the explored sector but always target its NET score
      // ("trust_gambling" or "trust_gambling|tend_trust" → "trust_gambling|net").
      if (variableType === "likert_grid" && isCarryable) {
        const [svId] = String(currentFilter).split("|");
        targetSubVar1 = `${svId}|net`;
      }

      this.setState({
        ActionMode: val,
        ActionTargetVariables: {
          TargetVariable1: selectedVariable,
          TargetVariable2: this.state.ActionTargetVariables?.TargetVariable2,
        },
        ActionTargetSubVariables: {
          TargetSubVariable1: targetSubVar1,
          TargetSubVariable2: this.state.ActionTargetSubVariables?.TargetSubVariable2,
        },
      });
      return;
    }
    this.setState({ ActionMode: val });
  };
  handlePossibleAreasChange     = (val) => this.setState({ PossibleAreas: val });
  clearAllDemos = () => this.setState({
    gender: null, age: null, ethnicity: null, party: null, education: null, segment: []
  });
  handlePreferenceFilterChange = (val) => {
    const viewMode = ["top", "second", "third"].includes(val) ? "top" : "heat";
    // In compare-waves mode, sync a specific category selection into compareKey
    // so the map automatically switches to "By category" mode for that key.
    const SPECIAL = new Set([null, "top", "second", "third", "net", "compare"]);
    const isSpecificKey = val && !SPECIAL.has(val) && !String(val).startsWith("compare_") && !String(val).includes("|");
    const compareUpdate = this.state.compareWaves && isSpecificKey ? { compareKey: val } : {};
    this.setState({ preferenceFilter: val, viewMode, ...compareUpdate });
  };
  handlehoverAreaChange     = (val) =>
    this.setState((prev) => (prev.hoverArea === val ? null : { hoverArea: val }));
  handlepoliInfoChange     = (val) => this.setState({ poliInfo: val });
  handlegeoLevelChange     = (val) => this.setState({ geoLevel: val });
  handlepoliInfoToplevelChange     = (val) => this.setState({ poliInfoToplevel: val });

filterByClient = (groupsById, clientName) => {
  if (!groupsById){return}
  return Object.fromEntries(
    Object.entries(groupsById).filter(([key, value]) => {
      const clients = Array.isArray(value.client) ? value.client : [value.client];
      return clients.some(c => slugify(c) === slugify(clientName));
    })
  );
};



  handlewaveSelectChange     = (val) => this.setState({ waveSelect: val });
  handlewaveOptionsChange     = (val) => this.setState({ waveOptions: val });
  handleCompareWavesChange = (val) => {
    if (!val) { this.setState({ compareWaves: false }); return; }
    // If a specific response key is already selected, jump straight to that category in compare mode
    const SPECIAL = new Set(["top", "second", "third", "net", "compare", null, undefined]);
    const currentFilter = this.state.preferenceFilter;
    const group = this.state.groupsById?.[this.state.selectedVariable];
    const isSpecificKey = currentFilter &&
      !SPECIAL.has(currentFilter) &&
      !String(currentFilter).startsWith("compare_") &&
      Array.isArray(group?.keys) && group.keys.includes(currentFilter);
    this.setState({ compareWaves: true, compareKey: isSpecificKey ? currentFilter : "__top__" });
  };
  handlewaveShowArrowsChange = ()=> this.setState({ showArrows: !this.state.showArrows });
  handleCompareKeyChange = (val) => {
    const isTopMover = val === "__top__" || val === "__top_neg__";
    if (isTopMover) {
      const group = this.state.groupsById?.[this.state.selectedVariable];
      const type = group?.type;
      const resetFilter = type === "likert" ? "net" : type === "likert_grid" ? "compare" : "top";
      this.setState({ compareKey: val, preferenceFilter: resetFilter });
    } else {
      this.setState({ compareKey: val });
    }
  };
  handleChatTabChange = (val) => this.setState({ activeTab: val });
  handleChatInputChange = (val) => this.setState({ chatInput: val });
  handleChatQueryOpenChange = (val) => this.setState({ chatQueryOpen: val });

  getChatMetricGroups = () => {
    const { groupsById, isDemoUser, user, dynamicSlug, activeClientSlug } = this.state;
    const demoVariant = String(user?.demo_variant || "default");
    const exploreGroups = this.filterByClient(groupsById, "all") || {};
    const clientSlug = dynamicSlug || activeClientSlug || this.props.slug;
    const slugGroups = this.filterByClient(groupsById, clientSlug) || {};
    const demoGroups = this.getDemoClientGroups(groupsById, demoVariant) || {};
    const clientGroups = isDemoUser ? demoGroups : slugGroups;
    return { exploreGroups, clientGroups };
  };

  // Return true if a metric belongs to a client page (client field is not "all")
  _isClientMetric = (metric) => {
    const def = this.state.groupsById?.[metric];
    if (!def) return false;
    const clients = Array.isArray(def.client) ? def.client : [def.client];
    return !clients.includes("all");
  };

  // Return the client URL slug for a metric (e.g. "stack-data-strategy")
  _clientSlugForMetric = (metric) => {
    const def = this.state.groupsById?.[metric];
    if (!def) return null;
    const clients = Array.isArray(def.client) ? def.client : [def.client];
    return clients.find(c => c && c !== "all") || null;
  };

  resolveSectionForChatMetric = (metric) => {
    if (!metric) return this.state.selectedSection;
    if (this._isClientMetric(metric) && this.clientIsAllowed()) return "client";
    return "explore";
  };

  getPrimaryMetricFromChatQuery = (queryUsed) => {
    if (!queryUsed || typeof queryUsed !== "object") return null;
    if (queryUsed.metric) return queryUsed.metric;
    if (Array.isArray(queryUsed.metrics) && queryUsed.metrics.length > 0) return queryUsed.metrics[0];
    if (Array.isArray(queryUsed.calls)) {
      const firstMetricCall = queryUsed.calls.find((call) => call?.metric) || null;
      if (firstMetricCall?.metric) return firstMetricCall.metric;
    }
    return null;
  };

  getTargetSectionFromChatQuery = (queryUsed, metric) => {
    const sourceSection =
      queryUsed?.source_section ||
      (Array.isArray(queryUsed?.calls)
        ? queryUsed.calls.find((call) => call?.source_section)?.source_section
        : null);

    if (sourceSection === "client" && this.clientIsAllowed()) return "client";
    if (sourceSection === "explore") return "explore";
    // Fall back to inspecting the metric's client field directly
    return this.resolveSectionForChatMetric(metric);
  };

  // Return the URL slug to navigate to for a client metric.
  // Uses query_used.client_slug if available, otherwise falls back to the metric definition.
  _getClientNavSlug = (queryUsed, metric) => {
    const fromQuery = queryUsed?.client_slug ||
      (Array.isArray(queryUsed?.calls)
        ? queryUsed.calls.find(c => c?.client_slug)?.client_slug
        : null);
    if (fromQuery) return fromQuery;
    return this._clientSlugForMetric(metric);
  };

  getChatPreferenceFilter = (metric, queryUsed) => {
    if (!metric || !this.state.groupsById?.[metric]) return null;
    const group = this.state.groupsById[metric];
    const sortKey = queryUsed?.highlight_sort_key || null;

    if (group.type === "likert_grid") {
      if (sortKey) {
        const subVarId = (group.subVariables || []).find((id) =>
          this.state.groupsById?.[id]?.keys?.includes(sortKey)
        );
        if (subVarId) return `${subVarId}|${sortKey}`;
      }
      return "compare";
    }

    if (sortKey && group.keys?.includes(sortKey)) return sortKey;
    if (group.type === "likert") return "net";
    if (group.type === "price_elast") return group?.polarity?.expect || null;
    return "top";
  };

  applyChatMetricSelection = (metric, queryUsed) => {
    if (!metric || !this.state.groupsById?.[metric]) return;
    const nextPreferenceFilter = this.getChatPreferenceFilter(metric, queryUsed);

    this.handleVariableChange(metric);

    if (nextPreferenceFilter) {
      const nextViewMode = ["top", "second", "third"].includes(nextPreferenceFilter) ? "top" : "heat";
      this.setState({
        preferenceFilter: nextPreferenceFilter,
        viewMode: nextViewMode,
      });
    }
  };

  handleChatHighlightAreas = (areas, queryUsed, question) => {
    // Geographic prompts: "where", "which constituencies", "highest/lowest" etc.
    const geoWords = /\b(where|which\s+(constituencies|areas|seats|places|regions|marginals?|held|conservative|labour|liberal|reform|safe)|map\s+of|show\s+me|top\s+\d+|highest|lowest|strongest|weakest|competitive|marginal|battleground|tightest|closest|prioritise|prioritize)\b/i;
    // Briefing/summary prompts: should answer only, no map mutation at all
    const briefingWords = /\b(briefing|meeting|meet\b|key findings|clearest findings|summary|summarise|summarize|talking points|preparing to meet|pitch(?:ing)?)\b/i;

    const questionText = question || "";
    const isBriefingPrompt = briefingWords.test(questionText);
    const isMapPrompt = !isBriefingPrompt && geoWords.test(questionText);

    const metric = this.getPrimaryMetricFromChatQuery(queryUsed);

    // A single-constituency drilldown (named seat query) always highlights,
    // regardless of question phrasing — "Would Labour win in Makerfield?" is
    // just as much a map action as "show me Makerfield".
    const isSingleConstituencyDrilldown = (queryUsed?.area_codes?.length === 1) && areas?.length === 1;

    // Switch variable/page whenever a metric is returned — including briefing prompts.
    // Briefing prompts still navigate to the relevant variable so the user can see it;
    // they just don't highlight the map or zoom (those remain gated on isMapPrompt below).
    // Don't switch context for demographic comparison queries — the metric is a chat-only
    // catalog entry (e.g. trust_industry) that doesn't exist as a map variable.
    const isDemoComparison = queryUsed?.tool === "query_demographic_comparison";
    const shouldSwitchContext = Boolean(metric) && !isDemoComparison && Boolean(this.state.groupsById?.[metric]);
    const shouldHighlight = areas?.length > 0 && (isMapPrompt || isSingleConstituencyDrilldown);
    const shouldZoom = shouldHighlight;

    const targetSection = shouldSwitchContext
      ? this.getTargetSectionFromChatQuery(queryUsed, metric)
      : this.state.selectedSection;

    const filters = queryUsed?.filters || {};

    // Single-constituency drilldown: zoom into the seat and switch to ward level,
    // replicating what happens when the user clicks a constituency on the map.
    const constituencyCode = isSingleConstituencyDrilldown ? areas[0] : null;

    this.setState({
      chatHighlightAreas: shouldHighlight ? areas : [],
      chatShouldZoom: shouldZoom,
      selectedAreaIdToZoom: constituencyCode ?? this.state.selectedAreaIdToZoom,
      pendingChatNavigation: shouldSwitchContext ? { metric, targetSection, queryUsed } : null,
      gender: filters.gender ?? null,
      age: filters.age ?? null,
      party: filters.party ?? null,
      education: filters.education ?? null,
    }, () => {
      if (!shouldSwitchContext) return;

      if (targetSection && targetSection !== this.state.selectedSection) {
        // Navigate to the correct page. For client metrics use the metric's own slug,
        // not dynamicSlug (which may point to the staff user's company page).
        if (targetSection === "client") {
          const clientNavSlug = this._getClientNavSlug(queryUsed, metric);
          if (clientNavSlug) {
            this.setThemeInUrl(clientNavSlug, { force: true });
            return;
          }
        }
        this.setThemeInUrl(targetSection, { force: true });
        return;
      }

      if (!metric || !this.state.groupsById?.[metric]) return;
      this.setState({ pendingChatNavigation: null }, () => {
        this.applyChatMetricSelection(metric, queryUsed);
      });
    });
  };

  handleChatSend = async (retryText) => {
    const text = String(retryText || this.state.chatInput || "").trim();
    if (!text || this.state.chatLoading) return;

    const userMsg = { role: "user", content: text, queryUsed: null };
    const nextMessages = retryText
      ? this.state.chatMessages
      : [...this.state.chatMessages, userMsg];

    this.setState({
      chatMessages: nextMessages,
      chatInput: retryText ? this.state.chatInput : "",
      chatLoading: true,
    });

    const history = nextMessages
      .slice(1)
      .slice(-8)
      .map((m) => ({ role: m.role, content: m.content }));

    try {
      const response = await axios.post(
        "/api/chat/",
        {
          message: text,
          context: this.buildChatContext(),
          history,
        },
        { withCredentials: true }
      );
      const data = response.data;
      const assistantMsg = {
        role: "assistant",
        content: data.answer || data.error || "No response.",
        queryUsed: data.out_of_scope ? null : data.query_used,
        sampleNote: data.sample_note || null,
        isError: !!data.error,
        logId: data.log_id || null,
        isClarification: !!data.is_clarification,
        clarificationOptions: data.is_clarification
          ? (data.answer || "").split("\n").filter(l => l.startsWith("- ")).map(l => l.slice(2))
          : [],
        clarificationQuestion: data.is_clarification
          ? (data.answer || "").split("\n")[0]
          : null,
      };

      this.setState((prev) => ({
        chatMessages: retryText
          ? [...prev.chatMessages.slice(0, -1), assistantMsg]
          : [...prev.chatMessages, assistantMsg],
        chatLoading: false,
      }));

      if (!data.out_of_scope && !data.error) {
        this.handleChatHighlightAreas(data.highlight_areas || [], data.query_used, text);
      }
    } catch (err) {
      const status = err.response?.status;
      const errMsg = status === 403
        ? "Chat is not available for your account."
        : status === 429
          ? "Too many requests — please wait a moment and try again."
          : status === 502 || status === 503
            ? "The AI service is temporarily unavailable. Please try again shortly."
            : "Something went wrong. Please try again.";

      this.setState((prev) => ({
        chatMessages: [
          ...prev.chatMessages,
          { role: "assistant", content: errMsg, queryUsed: null, isError: true, retryText: text },
        ],
        chatLoading: false,
      }));
    }
  };

  handleChatFlag = async (logId, category, note) => {
    try {
      await axios.post(
        "/api/chat/flag/",
        { log_id: logId, category, note },
        { withCredentials: true }
      );
    } catch (err) {
      console.warn("Flag submission failed:", err);
    }
  };

  buildChatContext = () => {
    const { exploreGroups, clientGroups } = this.getChatMetricGroups();
    const visibleGroups = { ...exploreGroups, ...clientGroups };
    const { groupsById } = this.state;
    const metric_catalog = {};

    Object.entries(visibleGroups).forEach(([key, val]) => {
      if (!val || val.hidden) return;
      // source_section is derived from the metric's own client field, not navigation state.
      // This means staff users on /explore still correctly see windfall as "client".
      const fullDef = groupsById?.[key] || val;
      const clientField = Array.isArray(fullDef.client) ? fullDef.client : [fullDef.client];
      const isClientOnly = !clientField.includes("all");
      metric_catalog[key] = {
        description: val.fulltext || val.label || key,
        keys: val.keys || [],
        labels: val.labels || {},
        schema: val.schema || "single",
        variable: key,
        polarity: val.polarity || null,
        source_section: isClientOnly ? "client" : "explore",
        client_slug: clientField.find(c => c && c !== "all") || null,
      };
    });

    return {
      constituency: this.state.selectedconstName,
      constituency_code: this.state.selectedconstCode,
      theme: this.state.selectedSection,
      level: this.state.geoLevel,
      metric_catalog,
    };
  };



  /**
   * Renders the sidebar Box wrapper + Sidebar component.
   * Both the explore and client sections use this; only groupsById
   * and a few section-specific extras differ between the two.
   */
  renderSidebar = (groups, selectedDemographics, extraProps = {}) => {
    const { isPanelOpen, sectionTitle, blurbText, selectedVariable, viewMode,
            preferenceFilter, gender, age, ethnicity, party, education, segment,
            rankedItems, theme_colours, colorLabels,waveSelect } = this.state;
    const selectedVariableResolved = extraProps.selectedVariable ?? selectedVariable;
    return (
      <Box
        sx={{
          width: isPanelOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED,
          flexShrink: 0,
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 1200,
          backgroundColor: '#fff',
        }}
        onMouseEnter={() => this.handlehoverAreaChange(null)}
        onMouseMove={() => this.handlehoverAreaChange(null)}
        onFocusCapture={() => this.handlehoverAreaChange(null)}
      >
        <Sidebar
          selectedFeatureData={this.state.selectedFeatureData}
          selectedAreaLabel={this.state.selectedFeature?.name}
          selectedconstName={this.state.selectedconstName}
          selectedFeature={this.state.selectedFeature}
          isPanelOpen={isPanelOpen}
          togglePanel={this.togglePanel}
          sectionTitle={sectionTitle}
          blurbText={blurbText}
          Datablurb={this.state.Datablurb}
          groupsById={(() => {
            if (!this.state.providerFilter || !this.state.providerFilterOriginalVar) return groups;
            const mockVar = this.state.groupsById?.[this.state.selectedVariable];
            const origVar = this.state.groupsById?.[this.state.providerFilterOriginalVar];
            if (!mockVar || !origVar) return groups;
            // Inject the mock variable but with the original's label so the dropdown looks right
            return { ...groups, [this.state.selectedVariable]: { ...mockVar, label: origVar.label, hidden: false } };
          })()}
          selectedVariable={selectedVariableResolved}
          onVariableChange={this.handleVariableChange}
          preferenceFilter={preferenceFilter}
          preferenceCategoryOptions={[]}
          preferenceCategoryLabels={{}}
          onPreferenceFilterChange={this.handlePreferenceFilterChange}
          viewMode={viewMode}
          genderFilter={gender}      onGenderChange={this.handleGenderChange}
          ageFilter={age}            onAgeChange={this.handleAgeChange}
          ethnicityFilter={ethnicity} onEthnicityChange={this.handleEthnicityChange}
          partyFilter={party}        onPartyChange={this.handlePartyChange}
          educationFilter={education} onEducationChange={this.handleEducationChange}
          segmentFilter={segment}    onSegmentChange={this.handleSegmentChange}
          onClearAll={this.clearAllDemos}
          theme_colours={theme_colours}
          selectedFeatureLevel={this.state.selectedFeature?.level || null}
          ActionTargetVariables={this.state.ActionTargetVariables}
          onActionTargetVariableChange={this.handleActionTargetVariableChange}
          ActionTargetSubVariables={this.state.ActionTargetSubVariables}
          handleActionTargetSubVariableChange={this.handleActionTargetSubVariableChange}
          ActionTargetRanges={this.state.ActionTargetRanges}
          ActionTargetMAXRanges={this.state.ActionTargetMAXRanges}
          handleActionTargetRangesChange={this.handleActionTargetRangesChange}
          ActionTargetConcentration={this.state.ActionTargetConcentration}
          handleActionTargetConcentrationChange={this.handleActionTargetConcentrationChange}
          audienceInsight={this.state.audienceInsight}
          audienceInsightLoading={this.state.audienceInsightLoading}
          onAudienceInsightRequest={this.handleAudienceInsightRequest}
          ActionMode={this.state.ActionMode}
          handleActionModeChange={this.handleActionModeChange}
          selectedDemographics={selectedDemographics}
          rankedItems={rankedItems}
          mapLoading={this.state.mapLoading}
          baselineLoading={this.state.baselineLoading}
          onAreaSelect={(area) => this.handleAreaSelect(area.id)}
          hoverArea={this.state.hoverArea}
          handlehoverAreaChange={this.handlehoverAreaChange}
          poliInfo={this.state.poliInfo}
          handlepoliInfoChange={this.handlepoliInfoChange}
          geoLevel={this.state.geoLevel}
          handlegeoLevelChange={this.handlegeoLevelChange}
          poliInfoToplevel={this.state.poliInfoToplevel}
          handlepoliInfoToplevelChange={this.handlepoliInfoToplevelChange}
          colorLabels={colorLabels}
          extDataBlurb={this.state.extDataBlurb}
          extDataBlurbComparison={this.state.extDataBlurbComparison}
          user={this.state.user}
          dynamicSlug={this.state.dynamicSlug}
          providerFilter={this.state.providerFilter}
          providerFilterOriginalVar={this.state.providerFilterOriginalVar}
          onProviderFilterChange={this.handleProviderFilterChange}
          providerFilter={this.state.providerFilter}
          providerFilterOriginalVar={this.state.providerFilterOriginalVar}
          onProviderFilterChange={this.handleProviderFilterChange}
          {...extraProps}
          waveSelect={waveSelect}
          compareWaves={this.state.compareWaves}
          showChatTab={ENABLE_CHAT && this.state.user?.is_staff === true && this.state.user?.company_slug === CHAT_ALLOWED_SLUG}
          chatHighlightCount={(this.state.chatHighlightAreas || []).length}
          onClearHighlights={() => this.setState({ chatHighlightAreas: [] })}
          activeTab={this.state.activeTab}
          onActiveTabChange={this.handleChatTabChange}
          chatMessages={this.state.chatMessages}
          chatInput={this.state.chatInput}
          onChatInputChange={this.handleChatInputChange}
          chatLoading={this.state.chatLoading}
          onChatSend={this.handleChatSend}
          onChatFlag={this.handleChatFlag}
          chatQueryOpen={this.state.chatQueryOpen}
          onChatQueryOpenChange={this.handleChatQueryOpenChange}
        />
      </Box>
    );
  };

  getDemoClientGroups = (groupsById, demoVariant = "default") => {
    const base = groupsById || {};
    const variant = String(demoVariant || "default").trim().toLowerCase();
    const groupIds = DEMO_VARIANT_CLIENT_GROUPS[variant] || DEMO_VARIANT_CLIENT_GROUPS.default || [];
    const keep = {};
    groupIds.forEach((id) => {
      if (base[id]) keep[id] = base[id];
      const subVars = base[id]?.subVariables || [];
      subVars.forEach((svId) => {
        if (base[svId]) keep[svId] = base[svId];
      });
    });
    return keep;
  };

  render() {
    const {
      isPanelOpen,
      sectionTitle,
      blurbText,
      selectedVariable, 
      viewMode,
      preferenceFilter,
      selectedSection,
      groupsById,
      selectedAreaIdToZoom,
      rankedItems,
      //selectedFeatureData,
      theme_colours,

      gender, age, ethnicity, party, education, segment,
    } = this.state;
    const hasActiveFilters = !!(gender || age || ethnicity || party || education || (Array.isArray(segment) ? segment.length > 0 : segment));
    const selectedDemographics = {
      gender: gender ? DEMO_LABELS.gender[gender] : null,
      age: age ? DEMO_LABELS.age[age] : null,
      ethnicity: ethnicity ? DEMO_LABELS.ethnicity[ethnicity] : null,
      party: party ? DEMO_LABELS.party[party] : null,
      education: education ? DEMO_LABELS.education[education] : null,
      // Segment codes are schema-specific — 4 is "Nostalgic Populists" on
      // tracking but "Octopus Energy" on energy — so resolve via the selected
      // question's schema rather than a fixed label map.
      segment: [].concat(segment || []).filter(Boolean)
        .map((v) => segmentLabel(groupsById?.[selectedVariable]?.schema, v))
        .filter(Boolean).join(", ") || null,
    };
    const clientName = this.state.dynamicSlug;
    const clientGroups = this.filterByClient(groupsById, clientName);
    // In highlights/hideChrome mode, expose the full groupsById so that
    // client-specific variables (e.g. tourist_tax_concerns) are reachable
    // by setHighlightsState without being stripped by the "all" client filter.
    const allGroups = this.props.hideChrome
      ? groupsById
      : this.filterByClient(groupsById, "all");
    // TEMP: hide Motability and WASPI variables from Stack users for the demo.
    // Flip either back to true to restore that client's questions.
    const STACK_CAN_SEE_MCCAIN = true;
    const STACK_CAN_SEE_MOTABILITY = true;
    const STACK_CAN_SEE_WASPI = true;
    const isStackUser = this.state.user?.company_slug === "stack-data-strategy";
    const hiddenCategories = new Set([
      ...(STACK_CAN_SEE_MOTABILITY ? [] : ["motability"]),
      ...(STACK_CAN_SEE_MCCAIN ? [] : ["mccain"]),
      ...(STACK_CAN_SEE_WASPI ? [] : ["waspi"]),
    ]);
    const rawSlugGroups = this.filterByClient(groupsById, this.props.slug);
    const slugGroups = (isStackUser && hiddenCategories.size)
      ? Object.fromEntries(Object.entries(rawSlugGroups || {}).filter(([, v]) => {
          // Match on category so shared groups (e.g. segment, which Motability
          // also uses) stay visible - only the client's own questions are dropped.
          if (hiddenCategories.has(String(v.category || "").toLowerCase())) return false;
          const clients = Array.isArray(v.client) ? v.client : [v.client];
          if (clients.includes("all")) return true;
          if (!STACK_CAN_SEE_MOTABILITY && clients.includes("motability-operations")) return false;
          if (!STACK_CAN_SEE_MCCAIN && clients.includes("mccain")) return false;
          if (!STACK_CAN_SEE_WASPI && clients.includes("waspi")) return false;
          return true;
        }))
      : rawSlugGroups;
    const isDemoUser = this.state.isDemoUser;
    const demoVariant = String(this.state.user?.demo_variant || "default");
    const demoClientGroups = this.getDemoClientGroups(groupsById, demoVariant);
    let clientGroupsForRender = isDemoUser ? demoClientGroups : slugGroups;
    // Inject mock provider variable into client groups so the map can find it
    if (this.state.providerFilter && this.state.selectedVariable && groupsById?.[this.state.selectedVariable]) {
      clientGroupsForRender = { ...clientGroupsForRender, [this.state.selectedVariable]: groupsById[this.state.selectedVariable] };
    }
    const demoClientKeys = Object.keys(demoClientGroups || {});
    const clientSelectedVariable = isDemoUser
      ? (demoClientKeys.includes(selectedVariable) ? selectedVariable : (demoClientKeys[0] || selectedVariable))
      : selectedVariable;
    const demoClientLocked = isDemoUser && demoVariant === "default";

    return (
      <Box sx={{ display: 'flex', height: this.props.hideChrome ? '100%' : '100vh' }}>
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          })}
        >
         
{!this.props.hideChrome && (
<HeaderBar
  THEME_CONFIGS={THEME_CONFIGS}
  selectedSection={selectedSection}
  setThemeInUrl={(v) => this.setThemeInUrl(v)}
  user={this.state.user}
  isDemoUser={this.state.isDemoUser}
  onLogout={this.handleLogout}
  clientIsAllowed={() => this.clientIsAllowed()}
  onTourRestart={() => {
    resetTour();
    this.applyTheme("explore");
    this.setState(s => ({ tourForceRun: (s.tourForceRun || 0) + 1 }));
  }}
/>
)}


          {selectedSection === "explore" && (
            <Box sx={{ display: "flex", flexGrow: 1, height: "100%", width: "100%" }}>
  <Box sx={{ flexGrow: 1, minWidth: 0, position: "relative" }}>
              
              <MapPage
            ref={this.mapRef}
            selectedSection={selectedSection}
            selectedGroupId={selectedVariable}
            preferenceFilter={preferenceFilter}
            groupsById={allGroups}
            disableInteraction={!!this.props.hideChrome}
            genderFilter={gender}
            ageFilter={age}
            ethnicityFilter={ethnicity}
            partyFilter={party}
            educationFilter={education}
            segmentFilter={segment}
            selectedDemographics={selectedDemographics}
            mode={viewMode}
            setBlurb={(blurb) => this.setState({ Datablurb: blurb })}
            theme_colours={theme_colours}
            onFeatureSelect={this.handleFeatureSelect}
            selectedconstCode={this.state.selectedconstCode}
            onClearAll={this.clearAllDemos}
            ActionTargetVariables={this.state.ActionTargetVariables}
            ActionTargetSubVariables={this.state.ActionTargetSubVariables}
            ActionTargetRanges = {this.state.ActionTargetRanges}
            ActionTargetConcentration={this.state.ActionTargetConcentration}
            ActionMode={this.state.ActionMode}

            handleActionTargetMAXRangesChange={this.handleActionTargetMAXRangesChange}
            onAudienceInsightChange={(val) => this.setState({ audienceInsight: val })}
            onAudienceInsightLoadingChange={(val) => this.setState({ audienceInsightLoading: val })}
            audienceInsightRequestId={this.state.audienceInsightRequestId}

            handlePossibleAreasChange={this.handlePossibleAreasChange}
            PossibleAreas={this.state.PossibleAreas}

            onRankedUpdate={this.handleRankedUpdate}
            onLoadingChange={this.handleMapLoadingChange}
            onBaselineLoadingChange={this.handleBaselineLoadingChange}
            selectedAreaIdToZoom={selectedAreaIdToZoom}

            hoverArea={this.state.hoverArea}
            handlehoverAreaChange={this.handlehoverAreaChange}
            poliInfo={this.state.poliInfo}
            handlepoliInfoChange={this.handlepoliInfoChange}
            geoLevel={this.state.geoLevel}
            handlegeoLevelChange={this.handlegeoLevelChange}
            poliInfoToplevel={this.state.poliInfoToplevel}
            handlepoliInfoToplevelChange={this.handlepoliInfoToplevelChange}
            colorLabels={this.state.colorLabels}
            colorMap={this.state.colorMap}


            
            waveSelect={this.state.waveSelect}
            handlewaveSelectChange={this.handlewaveSelectChange}

            waveOptions={this.state.waveOptions}
            handlewaveOptionsChange={this.handlewaveOptionsChange}
            compareWaves={this.state.compareWaves}
            onCompareWavesChange={this.handleCompareWavesChange}
            
        showArrows={this.state.showArrows}
        onShowArrowsToggle={this.handlewaveShowArrowsChange}
        compareKey={this.state.compareKey}
        onCompareKeyChange={this.handleCompareKeyChange}
        chatHighlightAreas={this.state.chatHighlightAreas || []}
        chatShouldZoom={this.state.chatShouldZoom}
        onClearChatHighlights={() => this.setState({ chatHighlightAreas: [], chatShouldZoom: false })}

              />
            {this.state.mapLoading && hasActiveFilters && (
              <Box sx={{
                position: "absolute", inset: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: "rgba(255,255,255,0.45)",
                backdropFilter: "blur(1px)",
                zIndex: 10,
                pointerEvents: "none",
              }}>
                <Box sx={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5,
                  bgcolor: "#fff", borderRadius: "12px", px: 3, py: 2.5,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}>
                  <Box sx={{
                    width: 24, height: 24, borderRadius: "50%",
                    border: "3px solid rgba(37,99,235,0.15)",
                    borderTopColor: "#2563eb",
                    animation: "spin 0.8s linear infinite",
                  }} />
                  <Box sx={{ fontSize: "12px", color: "#6b7280", fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                    Updating map…
                  </Box>
                </Box>
              </Box>
            )}

            {/* Report preview over the map — appears when a constituency is
                selected (where the ParliamentPlot sits at national level).
                Hidden while the full report popup is open. */}
            {!this.props.hideChrome && this.state.selectedFeature && this.state.selectedconstCode && !this.state.reportPreviewExpanded && (
              <MapReportPreview
                areaCode={this.state.selectedconstCode}
                constName={this.state.selectedconstName}
                reportType={this.state.previewReportType}
                onReportTypeChange={this.handlePreviewReportTypeChange}
                clientSlug={clientName}
                extDataBlurb={this.state.extDataBlurb}
                demographics={this.state.demographicSummary}
                mapImages={{ vote: this.state.voteMapImage, vi: this.state.viMapImage }}
                onOpen={this.openReportPreview}
                questionGroupId={selectedVariable}
                questionLabel={groupsById?.[selectedVariable]?.label
                  || groupsById?.[selectedVariable]?.fulltext}
                onOpenDrilldown={this.goToReportDrilldown}
              />
            )}
            </Box>
              {!this.props.hideChrome && this.renderSidebar(allGroups, selectedDemographics, {
                containerWidth: isPanelOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED,
                collapsedWidth: SIDEBAR_COLLAPSED,
              })}
          </Box>
          )}

          {selectedSection === "indicators" && (
              <IndicatorsPage
                theme_colours={theme_colours}
                user={this.state.user}
                isDemoUser={this.state.isDemoUser}
                onBack={() => this.setThemeInUrl("pol")}
              />
            )}

          {selectedSection === "report" && (
            <ReportPage
            clientSlug={clientName}
            groupsById={{ ...allGroups, ...clientGroups }}
                          constituencyOptions={this.state.PossibleAreas || []}
            isDemoUser={this.state.isDemoUser}
              initialConfig={this.state.reportInitialConfig}
              onConsumeInitialConfig={() => this.setState({ reportInitialConfig: null })}

              extDataBlurb={this.state.extDataBlurb}
              demographics={this.state.demographicSummary}
              mapImages={{
                vote: this.state.voteMapImage,
                vi: this.state.viMapImage,
              }}
              theme_colours={theme_colours}         
            />
          )}


          {selectedSection === "client" && this.clientIsAllowed() && (
           (!clientGroupsForRender ||
            Object.keys(clientGroupsForRender || {}).length === 0 ||
            this.state.isBootstrapping) ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                Loading client data...
              </Box>
            ) : (
            <Box sx={{ display: "flex", flexGrow: 1, height: "100%", width: "100%", position: "relative" }}>
              <Box
                sx={{
                  display: "flex",
                  flexGrow: 1,
                  width: "100%",
                  filter: demoClientLocked ? "blur(4px) saturate(0.9)" : "none",
                  pointerEvents: demoClientLocked ? "none" : "auto",
                  transition: "filter 0.2s ease",
                }}
              >
  <Box sx={{ flexGrow: 1, minWidth: 0, position: "relative" }}>
              
              <ClientPage
            selectedSection={selectedSection}   
            selectedGroupId={clientSelectedVariable}  
            preferenceFilter={preferenceFilter}   
            groupsById={clientGroupsForRender}
            genderFilter={gender}
            ageFilter={age}
            ethnicityFilter={ethnicity}
            partyFilter={party}
            educationFilter={education}
            segmentFilter={segment}
            selectedDemographics={selectedDemographics}
            mode={viewMode}
            setBlurb={(blurb) => this.setState({ Datablurb: blurb })}
            theme_colours={theme_colours}
            onFeatureSelect={this.handleFeatureSelect}
            selectedconstCode={this.state.selectedconstCode}
            onClearAll={this.clearAllDemos}

            ActionTargetVariables={this.state.ActionTargetVariables}
            ActionTargetSubVariables={this.state.ActionTargetSubVariables}
            ActionTargetRanges = {this.state.ActionTargetRanges}
            ActionTargetConcentration={this.state.ActionTargetConcentration}
            ActionMode={this.state.ActionMode}

            handleActionTargetMAXRangesChange={this.handleActionTargetMAXRangesChange}
            onAudienceInsightChange={(val) => this.setState({ audienceInsight: val })}
            onAudienceInsightLoadingChange={(val) => this.setState({ audienceInsightLoading: val })}
            audienceInsightRequestId={this.state.audienceInsightRequestId}

            handlePossibleAreasChange={this.handlePossibleAreasChange}
            PossibleAreas={this.state.PossibleAreas}

            onRankedUpdate={this.handleRankedUpdate}
            onLoadingChange={this.handleMapLoadingChange}
            onBaselineLoadingChange={this.handleBaselineLoadingChange}
            selectedAreaIdToZoom={selectedAreaIdToZoom}

            hoverArea={this.state.hoverArea}
            handlehoverAreaChange={this.handlehoverAreaChange}
            poliInfo={this.state.poliInfo}
            handlepoliInfoChange={this.handlepoliInfoChange}
            geoLevel={this.state.geoLevel}
            handlegeoLevelChange={this.handlegeoLevelChange}
            poliInfoToplevel={this.state.poliInfoToplevel}
            handlepoliInfoToplevelChange={this.handlepoliInfoToplevelChange}
            colorLabels={this.state.colorLabels}
            colorMap={this.state.colorMap}

            waveSelect={this.state.waveSelect}
            handlewaveSelectChange={this.handlewaveSelectChange}
            waveOptions={this.state.waveOptions}
            handlewaveOptionsChange={this.handlewaveOptionsChange}
            compareWaves={this.state.compareWaves}
            onCompareWavesChange={this.handleCompareWavesChange}
            showArrows={this.state.showArrows}
            onShowArrowsToggle={this.handlewaveShowArrowsChange}
            compareKey={this.state.compareKey}
            onCompareKeyChange={this.handleCompareKeyChange}
            chatHighlightAreas={this.state.chatHighlightAreas || []}
            chatShouldZoom={this.state.chatShouldZoom}
            onClearChatHighlights={() => this.setState({ chatHighlightAreas: [], chatShouldZoom: false })}

              />
            {this.state.mapLoading && hasActiveFilters && (
              <Box sx={{
                position: "absolute", inset: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: "rgba(255,255,255,0.45)",
                backdropFilter: "blur(1px)",
                zIndex: 10,
                pointerEvents: "none",
              }}>
                <Box sx={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5,
                  bgcolor: "#fff", borderRadius: "12px", px: 3, py: 2.5,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}>
                  <Box sx={{
                    width: 24, height: 24, borderRadius: "50%",
                    border: "3px solid rgba(37,99,235,0.15)",
                    borderTopColor: "#2563eb",
                    animation: "spin 0.8s linear infinite",
                  }} />
                  <Box sx={{ fontSize: "12px", color: "#6b7280", fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                    Updating map…
                  </Box>
                </Box>
              </Box>
            )}

            {/* Report preview over the map — appears when a constituency is
                selected. Hidden while the full report popup is open. */}
            {!this.props.hideChrome && this.state.selectedFeature && this.state.selectedconstCode && !this.state.reportPreviewExpanded && (
              <MapReportPreview
                areaCode={this.state.selectedconstCode}
                constName={this.state.selectedconstName}
                reportType={this.state.previewReportType}
                onReportTypeChange={this.handlePreviewReportTypeChange}
                clientSlug={clientName}
                extDataBlurb={this.state.extDataBlurb}
                demographics={this.state.demographicSummary}
                mapImages={{ vote: this.state.voteMapImage, vi: this.state.viMapImage }}
                onOpen={this.openReportPreview}
                questionGroupId={clientSelectedVariable}
                questionLabel={groupsById?.[clientSelectedVariable]?.label
                  || groupsById?.[clientSelectedVariable]?.fulltext}
                onOpenDrilldown={this.goToReportDrilldown}
              />
            )}
            </Box>
              {this.renderSidebar(clientGroupsForRender, selectedDemographics, {
                user: this.state.user,
                dynamicSlug: this.state.dynamicSlug,
                selectedVariable: clientSelectedVariable,
              })}
              </Box>
              {demoClientLocked && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                    zIndex: 12,
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: 520,
                      width: "100%",
                      borderRadius: 3,
                      bgcolor: "rgba(255,255,255,0.95)",
                      border: "1px solid rgba(15,23,42,0.12)",
                      boxShadow: "0 10px 40px rgba(2,6,23,0.18)",
                      px: 3,
                      py: 2.5,
                    }}
                  >
                    <Box sx={{ fontSize: "11px", fontWeight: 700, color: "#475569", letterSpacing: "0.08em", textTransform: "uppercase", mb: 0.5 }}>
                      Client Questions Preview
                    </Box>
                    <Box sx={{ fontSize: "15px", fontWeight: 600, color: "#0f172a", mb: 0.6 }}>
                      Subscriber feature
                    </Box>
                    <Box sx={{ fontSize: "13px", color: "#334155", lineHeight: 1.5 }}>
                      Once subscribed, your client-specific questions will appear here.
                    </Box>
                  </Box>
                </Box>
              )}
          </Box>
            ))}

        </Box>
        {!this.props.hideChrome && (
          <GuidedTour
            forceRun={this.state.tourForceRun}
            onNavigate={(section) => this.applyTheme(section)}
          />
        )}

        {/* Full report view — opened by clicking the map report preview */}
        <MapReportPopup
          open={this.state.reportPreviewExpanded && !!this.state.selectedFeature && !!this.state.selectedconstCode}
          areaCode={this.state.selectedconstCode}
          constName={this.state.selectedconstName}
          reportType={this.state.previewReportType || "default"}
          extDataBlurb={this.state.extDataBlurb}
          demographics={this.state.demographicSummary}
          mapImages={{ vote: this.state.voteMapImage, vi: this.state.viMapImage }}
          isDemoUser={this.state.isDemoUser}
          onClose={this.closeReportPreview}
          onSeeMoreReports={this.goToReportPage}
        />
      </Box>
    );
  }
}


function MainPageWithParams(props) {
  const { slug } = useParams();
  const navigate = useNavigate();
  return <MainPage {...props} slug={slug} navigate={navigate} />;
}

export { MainPage };
export default MainPageWithParams;
